import React, { useState, useEffect } from 'react';
import { MessageCircle, User, CheckCircle, Send, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductComments = ({ productId, productName }) => {
  const { isAuthenticated, token, user } = useAuth();
  const [comments, setComments] = useState([]);
  const [canComment, setCanComment] = useState(false);
  const [hasCommented, setHasCommented] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [productId, token]);

  const fetchComments = async () => {
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/comments/product/${productId}`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setComments(data.comments);
        setCanComment(data.can_comment);
        setHasCommented(data.has_commented);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/comments/product/${productId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: productId,
          comment: newComment.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Comment added!');
        setNewComment('');
        setCanComment(false);
        setHasCommented(true);
        // Add new comment to list
        setComments([data.comment, ...comments]);
      } else {
        toast.error(data.detail || 'Failed to add comment');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete your comment?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/comments/product/${productId}/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Comment deleted');
        setComments(comments.filter(c => c.id !== commentId));
        setCanComment(true);
        setHasCommented(false);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete comment');
      }
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Card className="bg-purple-800/50 border-purple-600 mt-8">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <MessageCircle className="w-6 h-6 text-yellow-400" />
          Customer Comments
          {comments.length > 0 && (
            <span className="text-purple-300 text-sm font-normal">
              ({comments.length})
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Comment Form - Only for verified purchasers */}
        {isAuthenticated ? (
          canComment ? (
            <form onSubmit={handleSubmitComment} className="mb-6">
              <div className="bg-purple-900/50 rounded-lg p-4 border border-purple-600">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 text-sm">Verified Purchase</span>
                </div>
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={`Share your experience with ${productName}...`}
                  className="bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400 min-h-[100px] mb-3"
                  data-testid="comment-input"
                />
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-yellow-500 hover:bg-yellow-600 text-purple-900"
                  data-testid="submit-comment-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Posting...' : 'Post Comment'}
                </Button>
              </div>
            </form>
          ) : hasCommented ? (
            <div className="mb-6 p-4 bg-purple-900/30 rounded-lg border border-purple-700">
              <p className="text-purple-300 text-sm">
                You have already shared your experience with this product.
              </p>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-purple-900/30 rounded-lg border border-purple-700">
              <p className="text-purple-300 text-sm">
                Purchase this product to share your experience.
              </p>
            </div>
          )
        ) : (
          <div className="mb-6 p-4 bg-purple-900/30 rounded-lg border border-purple-700">
            <p className="text-purple-300 text-sm">
              <Link to="/auth" className="text-yellow-400 hover:text-yellow-300 underline">
                Sign in
              </Link>
              {' '}to leave a comment. Only verified purchasers can comment.
            </p>
          </div>
        )}

        {/* Comments List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-pulse text-purple-300">Loading comments...</div>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="w-12 h-12 mx-auto text-purple-600 mb-3" />
            <p className="text-purple-300">No comments yet. Be the first to share your experience!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div
                key={comment.id}
                className="bg-purple-900/30 rounded-lg p-4 border border-purple-700"
                data-testid={`comment-${comment.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-700 flex items-center justify-center">
                      <User className="w-5 h-5 text-purple-300" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{comment.user_name}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-purple-400 text-xs">
                          {formatDate(comment.created_at)}
                        </span>
                        {comment.verified_purchase && (
                          <span className="flex items-center gap-1 text-green-400 text-xs">
                            <CheckCircle className="w-3 h-3" />
                            Verified Purchase
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Delete button for own comments */}
                  {isAuthenticated && user?.user_id === comment.user_id && (
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-purple-400 hover:text-red-400 transition-colors p-1"
                      title="Delete comment"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-purple-100 mt-3 pl-13">{comment.comment}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductComments;
